import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@heroui/react'
import clsx from 'clsx'
import type {
  Address,
  AddressIssue,
  AddressSuggestion
} from '@itsrever/returns-api-types'
import { useTheme } from '@/ui/theme'
import {
  currentValue,
  displayValue,
  fieldOf,
  formatSuggestion,
  isFixable
} from '@/hooks/use-validate-address'

interface AddressSuggestionModalProps {
  isOpen: boolean
  address: Address
  suggestions: AddressSuggestion[]
  issues: AddressIssue[]
  // The chosen locality candidate, if any, plus the field fixes left ticked.
  onAccept: (
    suggestion: AddressSuggestion | undefined,
    fixes: AddressIssue[]
  ) => void
  onKeep: () => void
  // Closes the modal without continuing, so the shopper can edit the form.
  onEdit: () => void
}

// Never a block: the shopper can always keep the address they typed. The page
// mounts it per answer, so the selection starts at the carrier's best candidate
// and every fix starts ticked.
export const AddressSuggestionModal = ({
  isOpen,
  address,
  suggestions,
  issues,
  onAccept,
  onKeep,
  onEdit
}: AddressSuggestionModalProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [selected, setSelected] = useState(0)
  const [unticked, setUnticked] = useState<Set<string>>(() => new Set())

  const selectedSuggestion = suggestions[selected]
  const fixes = issues.filter(isFixable)
  const hints = issues.filter((issue) => !isFixable(issue))
  const acceptedFixes = fixes.filter((fix) => !unticked.has(fixKey(fix)))
  // A merged street is one correction across two fields: clearing line 2 alone would
  // drop the street name.
  const fixRows = groupFixes(fixes)
  const canApply = Boolean(selectedSuggestion) || acceptedFixes.length > 0
  const onlyLocality = suggestions.length > 0 && issues.length === 0

  const toggleFix = (field: string) => {
    setUnticked((previous) => {
      const next = new Set(previous)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  const fieldLabel = (field: string) =>
    t(`address_validation.field.${field}`, { defaultValue: field })

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onKeep()
      }}
      isDismissable={false}
      hideCloseButton
      placement="center"
      size="md"
      scrollBehavior="inside"
      className="rounded-md border border-solid border-neutral-200"
      classNames={{ backdrop: 'backdrop-blur-sm' }}
    >
      <ModalContent>
        <ModalHeader className="text-base font-semibold">
          {t(
            onlyLocality
              ? 'address_validation.title'
              : 'address_validation.issues_title'
          )}
        </ModalHeader>
        <ModalBody>
          {suggestions.length > 0 ? (
            <>
              <p className="text-sm text-neutral-700">
                {t('address_validation.body', {
                  city: address.city ?? '',
                  postcode: address.postcode ?? '',
                  interpolation: { escapeValue: false }
                })}
              </p>
              <fieldset>
                <legend className="sr-only">
                  {t('address_validation.title')}
                </legend>
                <ul className="flex flex-col gap-2">
                  {suggestions.map((suggestion, index) => (
                    <li key={index}>
                      <label
                        className={clsx(
                          'flex w-full cursor-pointer rounded-md border border-solid px-3 py-2 text-left text-sm focus-within:ring-2 focus-within:ring-neutral-900',
                          index === selected
                            ? 'border-neutral-900 bg-neutral-100'
                            : 'border-neutral-300 bg-white'
                        )}
                      >
                        <input
                          type="radio"
                          name="address-suggestion"
                          value={index}
                          checked={index === selected}
                          onChange={() => setSelected(index)}
                          className="sr-only"
                        />
                        {formatSuggestion(address, suggestion)}
                      </label>
                    </li>
                  ))}
                </ul>
              </fieldset>
            </>
          ) : null}

          {issues.length > 0 ? (
            <p className="text-sm text-neutral-700">
              {t('address_validation.issues_body')}
            </p>
          ) : null}

          {fixes.length > 0 ? (
            <ul className="flex flex-col gap-2" data-testid="address-fixes">
              {fixRows.map((row) => {
                const key = fixKey(row[0])
                const checked = !unticked.has(key)
                return (
                  <li key={key}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      onClick={() => toggleFix(key)}
                      className={clsx(
                        'flex w-full items-start gap-3 rounded-md border border-solid px-3 py-2 text-left text-sm',
                        checked
                          ? 'border-neutral-900 bg-neutral-100'
                          : 'border-neutral-300 bg-white'
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={clsx(
                          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-sm border border-solid text-xs leading-none',
                          checked
                            ? 'border-neutral-900 bg-neutral-900 text-white'
                            : 'border-neutral-400 bg-white text-transparent'
                        )}
                      >
                        ✓
                      </span>
                      <span className="flex flex-col gap-1">
                        {row.map((fix) => (
                          <span key={fieldOf(fix)} className="flex flex-col">
                            <span>
                              <span className="font-medium">
                                {fieldLabel(fieldOf(fix))}:
                              </span>{' '}
                              {fix.suggested_value
                                ? displayValue(
                                    address,
                                    fieldOf(fix),
                                    fix.suggested_value
                                  )
                                : t('address_validation.clear_field')}
                            </span>
                            <span className="text-xs text-neutral-600">
                              {t('address_validation.you_wrote', {
                                value: currentValue(address, fieldOf(fix)),
                                interpolation: { escapeValue: false }
                              })}
                            </span>
                          </span>
                        ))}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : null}

          {hints.length > 0 ? (
            <ul
              className="flex flex-col gap-1 text-sm text-neutral-700"
              data-testid="address-hints"
            >
              {hints.map((hint) => (
                <li key={fieldOf(hint)}>
                  <span>
                    <span className="font-medium">
                      {fieldLabel(fieldOf(hint))}
                    </span>
                    {': '}
                    {t('address_validation.check_field')}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </ModalBody>
        <ModalFooter>
          {/* One child, so the footer's own row layout never squeezes three buttons together. */}
          <div className="flex w-full flex-col gap-2">
            {/* The portal's sm breakpoint is 375px: phones stack, from md the pair shares a row. */}
            <div className="flex w-full flex-col gap-2 md:flex-row">
              <Button
                variant="bordered"
                radius="sm"
                fullWidth
                onPress={onKeep}
                style={{
                  borderColor: theme.colors.primary.main,
                  color: theme.colors.primary.main,
                  borderRadius: theme.borderRadius
                }}
              >
                {t(
                  canApply
                    ? 'address_validation.keep_mine'
                    : 'address_validation.continue_anyway'
                )}
              </Button>
              {canApply ? (
                <Button
                  variant="solid"
                  radius="sm"
                  fullWidth
                  onPress={() => onAccept(selectedSuggestion, acceptedFixes)}
                  style={{
                    backgroundColor: theme.colors.primary.main,
                    color: theme.colors.primary.contrastText,
                    borderRadius: theme.borderRadius
                  }}
                >
                  {t(
                    onlyLocality
                      ? 'address_validation.use_suggestion'
                      : 'address_validation.use_corrected'
                  )}
                </Button>
              ) : null}
            </div>
            {hints.length > 0 ? (
              <Button
                variant="light"
                radius="sm"
                size="sm"
                fullWidth
                onPress={onEdit}
                style={{
                  color: theme.colors.primary.main,
                  borderRadius: theme.borderRadius
                }}
              >
                {t('address_validation.edit_address')}
              </Button>
            ) : null}
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// fixKey ties the two halves of a street merge to one switch.
const fixKey = (fix: AddressIssue): string =>
  fix.code === 'street_merged' ? 'street_merged' : fieldOf(fix)

const groupFixes = (fixes: AddressIssue[]): AddressIssue[][] => {
  const rows = new Map<string, AddressIssue[]>()
  for (const fix of fixes) {
    const key = fixKey(fix)
    rows.set(key, [...(rows.get(key) ?? []), fix])
  }
  return [...rows.values()]
}
